import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import IAbstractAuthenticator from './AbstractAuthenticator';

abstract class AbstractClient<A extends IAbstractAuthenticator> {
  private readonly httpClient: AxiosInstance;
  protected readonly authenticator: A;

  protected constructor(baseURL: string, authenticator: A) {
    this.authenticator = authenticator;
    this.httpClient = this.createHttpClient(baseURL);
  }

  protected createHttpClient(baseURL: string): AxiosInstance {
    const instance: AxiosInstance = axios.create({ baseURL });
    const retryDelay = (retryNumber = 0) => {
      const seconds = 2 ** retryNumber * 1000;
      const randomMs = 1000 * Math.random();
      return seconds + randomMs;
    };

    axiosRetry(instance, {
      retries: 1,
      retryDelay,
      // retry on Network Error & 5xx responses
      retryCondition: axiosRetry.isRetryableError,
    });

    instance.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          this.authenticator.revokeCredentials();
          this.authenticator.goToLogin();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }

  public rawGet<T = any>(url: string, config: AxiosRequestConfig = {}) {
    return this.httpClient.get<T>(url, config);
  }

  public rawPost<T = any>(url: string, data: any = {}, config: AxiosRequestConfig = {}) {
    return this.httpClient.post<T>(url, data, config);
  }

  public rawPut<T = any>(url: string, data: any, config: AxiosRequestConfig = {}) {
    return this.httpClient.put<T>(url, data, config);
  }

  public rawDelete<T = any>(url: string, config: AxiosRequestConfig = {}) {
    return this.httpClient.delete<T>(url, config);
  }

  public getHttpClient() {
    return this.httpClient;
  }

  public getAuthenticator() {
    return this.authenticator;
  }
}

export default AbstractClient;
